import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatZAR, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Sparkles, FileText, MessageSquare, Receipt, Plus, Trash2, CheckCircle2, Clock, Ban, Loader2, UserCheck, ShieldCheck, AlertCircle, Lock } from "lucide-react";
import StepProgressionPanel from "@/components/admin/StepProgressionPanel";
import DocIntelligencePanel from "@/components/admin/DocIntelligencePanel";
import MaintenanceDisclosurePanel from "@/components/admin/MaintenanceDisclosurePanel";
import WillDocumentActions from "@/components/admin/WillDocumentActions";
import TrustDocumentActions from "@/components/admin/TrustDocumentActions";
import AncDocumentActions from "@/components/admin/AncDocumentActions";
import OtpDocumentActions from "@/components/admin/OtpDocumentActions";
import TransferDocumentActions from "@/components/admin/TransferDocumentActions";
import ExternalDocumentsPanel from "@/components/admin/ExternalDocumentsPanel";
import LodgementReadinessPanel from "@/components/admin/LodgementReadinessPanel";
import EstateOverviewPanel from "@/components/EstateOverviewPanel";
import { toast } from "sonner";
import IntakeSummary from "@/components/admin/IntakeSummary";

const STATUSES = ["new_lead", "awaiting_documents", "in_progress", "lodged", "completed"];

export default function MatterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showDoc, setShowDoc] = useState(null);
  const [tplId, setTplId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [invItems, setInvItems] = useState([{ description: "Conveyancing fee", amount: 0 }]);
  const [invNotes, setInvNotes] = useState("");
  const [extractingId, setExtractingId] = useState(null);
  const [structuredTemplates, setStructuredTemplates] = useState([]);
  const [showStructuredGen, setShowStructuredGen] = useState(false);
  const [structuredKey, setStructuredKey] = useState("");
  const [generatingStructured, setGeneratingStructured] = useState(false);
  const [missingFieldsDlg, setMissingFieldsDlg] = useState(null);

  // Phase 8.2 — ownership
  const [me, setMe] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [ownerSaving, setOwnerSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/matters/${id}`);
    setData(data);
  };

  useEffect(() => {
    load();
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/admin/doc-templates").then((r) => setStructuredTemplates(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/users/assignable").then((r) => setAssignableUsers(r.data)).catch(() => {});
  }, [id]);

  const setOwner = async (ownerUserId) => {
    if (ownerSaving) return;
    setOwnerSaving(true);
    try {
      const { data: res } = await api.patch(`/matters/${id}/owner`, {
        owner_user_id: ownerUserId,
      });
      if (res.changed) {
        toast.success(
          ownerUserId
            ? `Assigned to ${res.owner_label}`
            : "Ownership cleared"
        );
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOwnerSaving(false);
    }
  };

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  const { matter, documents, notifications, invoices, uploads = [], billing_summary, accountability } = data;

  const changeStatus = async (status) => {
    try {
      await api.patch(`/matters/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateDoc = async () => {
    if (!tplId) return;
    setGenerating(true);
    try {
      await api.post("/documents/generate", { matter_id: id, template_id: tplId });
      toast.success("Document drafted");
      setShowGen(false);
      setTplId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const createInvoice = async () => {
    try {
      const items = invItems.filter((i) => i.description && i.amount > 0);
      if (items.length === 0) return toast.error("Add at least one line item");
      await api.post("/invoices", { matter_id: id, items, notes: invNotes });
      toast.success("Invoice created");
      setShowInvoice(false);
      setInvItems([{ description: "Conveyancing fee", amount: 0 }]);
      setInvNotes("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const transitionInvoice = async (invId, target) => {
    try {
      const endpoint = target === "issued"
        ? `/admin/invoices/${invId}/issue`
        : `/admin/invoices/${invId}/mark-paid`;
      await api.post(endpoint);
      toast.success(target === "issued" ? "Invoice issued" : "Marked as paid");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runOtpExtraction = async (uploadId) => {
    if (extractingId) return;
    setExtractingId(uploadId);
    try {
      // Phase 6A.2 — endpoint returns 202 with job_id immediately.
      // We then poll until the job is completed/failed.
      const { data: dispatch } = await api.post(`/admin/matters/${id}/uploads/${uploadId}/extract`);
      if (!dispatch.job_id) {
        toast.success(dispatch.audit_event?.summary || "Extraction completed");
        load();
        return;
      }
      toast.info("Processing document…");
      const jobId = dispatch.job_id;
      const maxAttempts = 60;          // 60 × 5s = 5 minutes
      let final = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: job } = await api.get(`/admin/extraction-jobs/${jobId}`);
          if (job.status === "completed") { final = job; break; }
          if (job.status === "failed")    { final = job; break; }
          // still queued / processing — keep polling
        } catch (pollErr) {
          // 401/404 etc — abort polling
          break;
        }
      }
      if (!final) {
        toast.warning("Extraction is still running. The page will update once it finishes.");
      } else if (final.status === "completed") {
        toast.success(final.result_summary || "OTP extracted");
      } else {
        toast.error(`Extraction failed: ${final.error || "unknown error"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtractingId(null);
    }
  };

  // Phase 6B — deterministic, template-based document generation from canonical entities.
  const generateStructured = async (templateKey, fromActionLabel = null) => {
    if (!templateKey || generatingStructured) return;
    setGeneratingStructured(true);
    try {
      const { data: out } = await api.post(
        `/admin/matters/${id}/documents/generate-structured`,
        { template_key: templateKey, from_action_label: fromActionLabel },
      );
      toast.success(out.audit_event.summary);
      setShowStructuredGen(false);
      setStructuredKey("");
      load();
    } catch (e) {
      // Validation 422 → show what's missing instead of a generic toast
      const status = e?.response?.status;
      const body   = e?.response?.data;
      if (status === 422 && body?.blocked) {
        setShowStructuredGen(false);
        setMissingFieldsDlg({
          templateKey,
          templateLabel: structuredTemplates.find((t) => t.key === templateKey)?.label || templateKey,
          missing: body.missing || [],
          message: body.message,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setGeneratingStructured(false);
    }
  };

  const intake = matter.intake_data || {};

  return (
    <div className="p-10 max-w-6xl mx-auto" data-testid="matter-detail">
      <Link to="/admin/matters" className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-6">
        <ArrowLeft className="h-3 w-3" /> Back to matters
      </Link>

      <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
        <div>
          <div className="text-overline text-zinc-500 capitalize">{matter.service_type}</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">
            {matter.client_name || "Unnamed matter"}
          </h1>
          <div className="text-sm text-zinc-500 mt-2">
            Created {new Date(matter.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-overline text-zinc-500">Status</div>
          <Select value={matter.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-48 rounded-none" data-testid="matter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Phase 8.2 — Ownership bar */}
      <OwnerBar
        matter={matter}
        me={me}
        assignableUsers={assignableUsers}
        saving={ownerSaving}
        onSet={setOwner}
      />

      {/* P0 — Legal Readiness panel */}
      <LegalReadinessPanel matterId={id} matter={matter} onChanged={load} />

      {/* P0 — Step progression: milestones + timeline override */}
      <StepProgressionPanel matterId={id} matter={matter} accountability={accountability} onChanged={load} />

      {/* Phase 10.1 — Estate Overview (deceased_estate matters from calculator) */}
      <EstateOverviewPanel matter={matter} role="admin" onChanged={load} />

      {/* Phase 9 — Maintenance & Divorce Support disclosure + J941 */}
      <MaintenanceDisclosurePanel matterId={id} matter={matter} />

      {/* Phase 5.6 — Risk banner (always visible across tabs) */}
      <RiskBanner risks={matter.risks || []} />

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 w-full justify-start p-0 h-auto">
          <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="entities" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-entities">
            Entities &amp; risks
          </TabsTrigger>
          <TabsTrigger value="checklist" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-checklist">
            Checklist ({(matter.tasks || []).length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-documents">
            Documents ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="comms" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-comms">
            Comms ({(notifications || []).length + ((matter.audit_events || []).length)})
          </TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-none data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-6 py-3" data-testid="tab-invoices">
            Invoices ({invoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 p-8">
              <div className="text-overline text-zinc-500 mb-4">Intake summary</div>
              <IntakeSummary intake={intake} entities={matter.entities} />
            </div>
            <div className="space-y-4">
              <KeyVal label="Service" value={matter.service_type} />
              <KeyVal label="Status" value={matter.status.replace("_", " ")} />
              <KeyVal label="Risk" value={matter.risk_profile || "—"} />
              <KeyVal label="Value" value={matter.value ? formatZAR(matter.value) : "—"} />
              <KeyVal label="Client" value={matter.client_name} />
              <KeyVal label="Email" value={intake?.client?.email || "—"} />
              <KeyVal label="Phone" value={intake?.client?.phone || "—"} />
              {matter.portal_token && (
                <div className="bg-zinc-900 text-white p-4">
                  <div className="text-overline text-zinc-400">Client portal</div>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/client/${matter.portal_token}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Portal link copied");
                    }}
                    className="mt-2 text-xs font-mono text-zinc-300 hover:text-white truncate w-full text-left"
                    data-testid="copy-portal-link"
                  >
                    /client/{matter.portal_token.slice(0, 12)}…
                  </button>
                  <a
                    href={`/client/${matter.portal_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs underline text-zinc-300 hover:text-white"
                  >
                    Open ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="entities" className="mt-6">
          <EntitiesPanel
            matter={matter}
            onGenerate={(templateKey, label) => generateStructured(templateKey, label)}
            generating={generatingStructured}
          />
        </TabsContent>

        <TabsContent value="checklist" className="mt-6">
          <ChecklistPanel
            matterId={id}
            tasks={matter.tasks || []}
            onChange={load}
          />
        </TabsContent>


        <TabsContent value="documents" className="mt-6">
          {matter.doc_generation && matter.doc_generation.missing_critical?.length > 0 && (
            <div
              className="mb-6 border border-amber-300 bg-amber-50 px-5 py-4 flex items-start gap-3"
              data-testid="docs-critical-missing-banner"
            >
              <Ban className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">
                  Cannot generate document — missing required information
                </div>
                <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                  Complete these fields before the draft can be produced for review:
                </div>
                <ul className="text-xs text-amber-900 mt-2 space-y-1 list-disc pl-5">
                  {matter.doc_generation.missing_critical.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {matter.portal_token && (
                  <a
                    href={`/client/${matter.portal_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-3 text-xs underline text-amber-900 hover:text-amber-700"
                    data-testid="docs-open-portal-link"
                  >
                    Open client portal to capture missing details ↗
                  </a>
                )}
              </div>
            </div>
          )}
          {uploads.length > 0 && (
            <div className="mb-6" data-testid="uploads-panel">
              <div className="text-overline text-zinc-500 mb-3">Client / OTP uploads</div>
              <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
                {uploads.map((u) => {
                  const isOtp     = u.document_type === "otp";
                  const status    = u.extraction_status || "pending";
                  const isPending = status === "pending";
                  const isProcessing = status === "processing";
                  const isPendingOcr = status === "pending_ocr";
                  const isExtracted = status === "extracted";
                  const isAccepted  = status === "accepted";
                  const isFailed  = status === "failed";
                  const inFlight = isProcessing || isPendingOcr;
                  const supportsDocIntel = [
                    "fica_id", "fica_poa", "death_certificate", "will_pdf", "trust_deed",
                  ].includes(u.document_type);
                  const showDocIntel = supportsDocIntel && (isExtracted || isAccepted);
                  const badgeTone =
                    isAccepted    ? "bg-emerald-200 text-emerald-900" :
                    isExtracted   ? "bg-emerald-100 text-emerald-800" :
                    isFailed      ? "bg-red-100 text-red-700" :
                    isPendingOcr  ? "bg-amber-100 text-amber-800" :
                    isProcessing  ? "bg-blue-100 text-blue-800" :
                    isPending     ? "bg-blue-50 text-blue-700" :
                                    "bg-zinc-100 text-zinc-700";
                  const statusLabel =
                    isAccepted   ? "AI: accepted" :
                    isExtracted  ? "AI extracted — review →" :
                    isPendingOcr ? "running OCR…" :
                    isProcessing ? "processing…" :
                    isPending    ? "AI extracting…" :
                    status;
                  return (
                    <div key={u.id} className="flex flex-col" data-testid={`upload-row-${u.id}`}>
                      <div className="p-4 flex flex-wrap items-center gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="font-medium text-sm">{u.name}</div>
                          <div className="text-[11px] text-zinc-500 mt-1">
                            {(u.document_type || "other").toUpperCase()} · {Math.round((u.size || 0) / 1024)} KB
                            {u.uploaded_at && <> · uploaded {new Date(u.uploaded_at).toLocaleString()}</>}
                          </div>
                          {isFailed && u.extraction_error && (
                            <div className="mt-1 text-[11px] text-red-700" data-testid={`upload-error-${u.id}`}>
                              {u.extraction_error}
                            </div>
                          )}
                        </div>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${badgeTone}`}
                              data-testid={`extraction-status-${u.id}`}>
                          {statusLabel}
                        </span>
                        {isOtp && (isPending || isFailed) && !inFlight && (
                          <Button
                            size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                            onClick={() => runOtpExtraction(u.id)}
                            disabled={extractingId === u.id}
                            data-testid={`extract-otp-btn-${u.id}`}
                          >
                            {extractingId === u.id
                              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Processing…</>
                              : <><Sparkles className="h-3 w-3 mr-2" /> {isFailed ? "Retry extraction" : "Run OTP extraction"}</>}
                          </Button>
                        )}
                      </div>
                      {showDocIntel && (
                        <DocIntelligencePanel
                          uploadId={u.id}
                          documentType={u.document_type}
                          onAccepted={() => load()}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-zinc-600">AI-drafted documents based on matter data. Each carries a "draft — requires legal review" stamp.</p>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowStructuredGen(true)}
                variant="outline"
                className="rounded-none"
                data-testid="generate-structured-doc-btn"
              >
                <FileText className="h-4 w-4 mr-2" /> Generate from canonical entities
              </Button>
              <Button onClick={() => setShowGen(true)} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="generate-doc-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Generate document
              </Button>
            </div>
          </div>

          {/* Will-discipline quick actions (Phase Wills-Fidelity) */}
          <WillDocumentActions matter={matter} documents={documents} onChanged={load} />

          {/* Trust-discipline quick actions (Phase Trust-1) */}
          <TrustDocumentActions matter={matter} documents={documents} onChanged={load} />

          {/* ANC-discipline quick actions (Phase ANC-1) */}
          <AncDocumentActions matter={matter} documents={documents} onChanged={load} />

          {/* OTP-discipline quick actions (Phase OTP-1) */}
          <OtpDocumentActions matter={matter} documents={documents} onChanged={load} />

          {/* Conveyancing transfer discipline (Phase Conv-1a) */}
          <TransferDocumentActions matter={matter} documents={documents} onChanged={load} />

          {/* External documents tracking (Phase Conv-1b.2) — TYPE 2 uploads */}
          <ExternalDocumentsPanel matter={matter} onChanged={load} />

          {/* Lodgement Readiness — % + checklist + Ready-to-Lodge gate (Phase Conv-1b.2) */}
          <LodgementReadinessPanel matter={matter} refreshSignal={documents?.length} />
          <div className="bg-white border border-zinc-200">
            {documents.length === 0 ? (
              <div className="p-10 text-center text-zinc-500" data-testid="docs-empty-state">
                <FileText className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
                {matter.doc_generation && !matter.doc_generation.can_generate ? (
                  <>
                    <p className="text-base font-semibold text-zinc-900">
                      This document cannot be generated until required information is completed.
                    </p>
                    <p className="text-xs text-zinc-500 mt-2">
                      We need the following to produce a notary-grade draft:
                    </p>
                    <ul className="text-sm text-amber-800 mt-3 inline-block text-left list-disc pl-5">
                      {matter.doc_generation.missing_critical.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                    {matter.portal_token && (
                      <div className="mt-5 flex flex-wrap gap-2 justify-center">
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/matters/${matter.id}/email-missing-summary`);
                              toast.success("Missing-data summary sent", {
                                description: `Simulated to ${data.to || "client"} — ${data.missing.length} field(s).`,
                              });
                            } catch (e) {
                              toast.error("Could not send", { description: e?.response?.data?.detail || e.message });
                            }
                          }}
                          className="px-4 py-2 text-xs uppercase tracking-wider font-medium bg-zinc-900 text-white hover:bg-zinc-800"
                          data-testid="docs-email-summary-btn"
                        >
                          Email missing-data summary
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/client/${matter.portal_token}`;
                            navigator.clipboard.writeText(url);
                            toast.success("Portal link copied");
                          }}
                          className="px-4 py-2 text-xs uppercase tracking-wider font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                          data-testid="docs-empty-copy-portal-btn"
                        >
                          Copy portal link
                        </button>
                      </div>
                    )}
                    {matter.reminder_summary && matter.reminder_summary.active_count > 0 && (
                      <div className="mt-4 text-[11px] text-zinc-500 flex items-center justify-center gap-2"
                           data-testid="docs-reminder-status">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wide border ${
                          matter.reminder_summary.escalated
                            ? "bg-rose-100 text-rose-800 border-rose-200"
                            : "bg-zinc-100 text-zinc-700 border-zinc-300"
                        }`}>
                          {matter.reminder_summary.latest_stage_label || "Reminder sent"}
                        </span>
                        {matter.reminder_summary.last_sent_at && (
                          <span>Last sent {new Date(matter.reminder_summary.last_sent_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p>
                    No documents yet. Click "Generate document" to draft one from a template.
                  </p>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-zinc-200">
                {documents.map((d) => {
                  const statusTone =
                    d.status === "sent"      ? "bg-emerald-100 text-emerald-800" :
                    d.status === "reviewed"  ? "bg-blue-100 text-blue-800" :
                                               "bg-amber-100 text-amber-800";
                  const statusLabel = (d.status || "").replace(/_/g, " ");
                  return (
                    <li key={d.id} className="p-5 flex items-center justify-between hover:bg-zinc-50">
                      <div>
                        <div className="font-medium">{d.template_name}</div>
                        <div className="text-xs text-zinc-500">
                          Generated {new Date(d.created_at).toLocaleString()}
                          {d.last_edited_at && <> · Last edited {new Date(d.last_edited_at).toLocaleString()}</>}
                          {d.sent_at && d.sent_to && <> · Sent to {d.sent_to}</>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${statusTone}`}>
                          {statusLabel}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                          onClick={() => navigate(`/admin/matters/${id}/documents/${d.id}`)}
                          data-testid={`open-doc-${d.id}`}
                        >
                          Open workspace
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="comms" className="mt-6">
          <p className="text-sm text-zinc-600 mb-4">
            Unified chronological feed: simulated comms + system events (intake, extraction, risk detection, document generation, review tasks).
          </p>
          {(() => {
            const events = [
              ...((notifications || []).map((n) => ({
                kind: "comm", id: n.id, channel: n.channel, trigger: n.trigger,
                content: n.content, to: n.to, status: n.status,
                ts: n.created_at,
              }))),
              ...((matter.audit_events || []).map((a) => ({
                kind: "system", id: a.id, system_kind: a.kind, actor: a.actor,
                content: a.summary, ts: a.created_at,
                risks_added: a.risks_added || [], actions_added: a.actions_added || [],
                template_key: a.template_key, document_id: a.document_id,
                from_action_label: a.from_action_label,
              }))),
            ].sort((x, y) => (y.ts || "").localeCompare(x.ts || ""));
            return (
              <div className="bg-white border border-zinc-200" data-testid="comms-feed">
                {events.length === 0 ? (
                  <div className="p-10 text-center text-zinc-500">
                    <MessageSquare className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
                    <p>No events yet.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-zinc-200">
                    {events.map((ev) => (
                      <li key={`${ev.kind}-${ev.id}`} className="p-5"
                          data-testid={`comms-event-${ev.kind}-${ev.id}`}>
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {ev.kind === "comm" ? (
                            <>
                              <span className="text-[10px] tracking-widest uppercase bg-zinc-100 px-2 py-1">{ev.channel}</span>
                              <span className="text-[10px] tracking-widest uppercase bg-amber-100 text-amber-800 px-2 py-1">would send</span>
                              <span className="text-xs text-zinc-500">{ev.trigger}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-[10px] tracking-widest uppercase bg-zinc-900 text-white px-2 py-1">system</span>
                              <span className="text-[10px] tracking-widest uppercase bg-zinc-100 px-2 py-1">{(ev.system_kind || "").replace(/_/g, " ")}</span>
                              {ev.actor && <span className="text-xs text-zinc-500">{ev.actor}</span>}
                            </>
                          )}
                          <span className="text-xs text-zinc-400 ml-auto">{ev.ts ? new Date(ev.ts).toLocaleString() : ""}</span>
                        </div>
                        <div className="text-sm text-zinc-700">{ev.content}</div>
                        {ev.kind === "comm" && ev.to && (
                          <div className="text-xs text-zinc-500 mt-1">→ {ev.to}</div>
                        )}
                        {ev.kind === "system" && ((ev.risks_added && ev.risks_added.length > 0) || (ev.actions_added && ev.actions_added.length > 0)) && (
                          <div className="mt-2 text-[11px] text-zinc-600 space-y-0.5">
                            {ev.risks_added.length > 0 && (
                              <div><b>Risks added:</b> {ev.risks_added.join(", ")}</div>
                            )}
                            {ev.actions_added.length > 0 && (
                              <div><b>Actions added:</b> {ev.actions_added.join("; ")}</div>
                            )}
                          </div>
                        )}
                        {ev.kind === "system" && ev.from_action_label && (
                          <div className="text-[11px] text-zinc-500 mt-1 italic">triggered from action: "{ev.from_action_label}"</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <div className="flex justify-between items-start mb-4 gap-6">
            <div>
              <p className="text-sm text-zinc-600">Billing for this matter — auto-generated by system events, plus any manual line-item invoices.</p>
              {billing_summary && (
                <div className="mt-3 flex gap-6 text-sm" data-testid="billing-summary">
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500">Outstanding</div>
                    <div className="font-serif text-2xl text-zinc-900">{formatZAR(billing_summary.outstanding_total)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500">Paid</div>
                    <div className="font-serif text-2xl text-green-700">{formatZAR(billing_summary.paid_total)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500">Invoices</div>
                    <div className="font-serif text-2xl text-zinc-700">{billing_summary.count}</div>
                  </div>
                </div>
              )}
            </div>
            <Button onClick={() => setShowInvoice(true)} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="create-invoice-btn">
              <Plus className="h-4 w-4 mr-2" /> New invoice
            </Button>
          </div>
          <div className="bg-white border border-zinc-200">
            {invoices.length === 0 ? (
              <div className="p-10 text-center text-zinc-500">
                <Receipt className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
                <p>No invoices yet. They'll appear here automatically as the matter progresses.</p>
              </div>
            ) : (
              <ul className="divide-y divide-zinc-200">
                {invoices.map((inv) => {
                  const status = inv.status || "draft";
                  const badgeCls = status === "paid" ? "bg-green-100 text-green-800"
                                 : status === "issued" ? "bg-blue-100 text-blue-800"
                                 : "bg-amber-100 text-amber-800";
                  const isAuto = inv.kind && inv.kind !== "manual";
                  return (
                    <li key={inv.id} className="p-5 flex items-center justify-between gap-4" data-testid={`invoice-row-${inv.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <div className="font-medium font-mono text-sm">{inv.invoice_number}</div>
                          {isAuto && (
                            <span className="text-[10px] tracking-widest uppercase text-zinc-400">auto · {inv.kind?.replace(/_/g, " ").replace(/:/g, " → ")}</span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-700 mt-0.5">{inv.description || (inv.items?.[0]?.description) || "Invoice"}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                          Created {new Date(inv.created_at).toLocaleDateString()}
                          {inv.issued_at && <> · Issued {new Date(inv.issued_at).toLocaleDateString()}</>}
                          {inv.paid_at && <> · Paid {new Date(inv.paid_at).toLocaleDateString()}</>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-2xl">{formatZAR(inv.amount ?? inv.total)}</span>
                        <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${badgeCls}`}>{status}</span>
                        {status === "draft" && (
                          <Button size="sm" variant="outline" className="rounded-none" data-testid={`issue-invoice-${inv.id}`}
                                  onClick={() => transitionInvoice(inv.id, "issued")}>Issue</Button>
                        )}
                        {status === "issued" && (
                          <Button size="sm" className="rounded-none bg-green-700 hover:bg-green-800" data-testid={`mark-paid-${inv.id}`}
                                  onClick={() => transitionInvoice(inv.id, "paid")}>Mark paid</Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Generate document dialog */}
      <Dialog open={showGen} onOpenChange={setShowGen}>
        <DialogContent className="rounded-none" data-testid="gen-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Generate document</DialogTitle>
            <DialogDescription>Pick a template — AI will populate it with the matter data.</DialogDescription>
          </DialogHeader>
          <Select value={tplId} onValueChange={setTplId}>
            <SelectTrigger className="rounded-none" data-testid="template-select">
              <SelectValue placeholder="Choose template…" />
            </SelectTrigger>
            <SelectContent>
              {templates.filter((t) => !t.service_type || t.service_type === matter.service_type).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={generateDoc} disabled={!tplId || generating} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-generate">
            {generating ? "Drafting with AI…" : "Generate draft"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Phase 6B — Deterministic document generation from canonical entities */}
      <Dialog open={showStructuredGen} onOpenChange={setShowStructuredGen}>
        <DialogContent className="rounded-none" data-testid="gen-structured-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Generate from canonical entities</DialogTitle>
            <DialogDescription>
              Deterministic template rendering. No freeform AI — only validated structured data.
              Each generated document creates a lawyer-review task.
            </DialogDescription>
          </DialogHeader>
          <Select value={structuredKey} onValueChange={setStructuredKey}>
            <SelectTrigger className="rounded-none" data-testid="structured-template-select">
              <SelectValue placeholder="Choose template…" />
            </SelectTrigger>
            <SelectContent>
              {structuredTemplates
                .filter((t) => !t.matter_types?.length || t.matter_types.includes(matter.matter_type || matter.service_type))
                .map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`structured-template-${t.key}`}>
                    {t.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {structuredTemplates.length > 0 &&
            structuredTemplates.filter((t) => !t.matter_types?.length || t.matter_types.includes(matter.matter_type || matter.service_type)).length === 0 && (
              <div className="text-xs text-zinc-500" data-testid="no-structured-tpl-for-type">
                No structured templates are configured for matter type "{matter.matter_type || matter.service_type}".
              </div>
          )}
          <Button
            onClick={() => generateStructured(structuredKey)}
            disabled={!structuredKey || generatingStructured}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800"
            data-testid="confirm-generate-structured"
          >
            {generatingStructured ? "Rendering…" : "Render document"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Phase 6B — Missing required entities dialog (blocking validation feedback) */}
      <Dialog open={!!missingFieldsDlg} onOpenChange={() => setMissingFieldsDlg(null)}>
        <DialogContent className="rounded-none" data-testid="missing-fields-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cannot generate — required data missing</DialogTitle>
            <DialogDescription>
              {missingFieldsDlg?.message ||
                "Required canonical entities are missing for this template."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <div className="text-overline text-zinc-500 mb-2">{missingFieldsDlg?.templateLabel}</div>
            <ul className="space-y-2 text-sm">
              {(missingFieldsDlg?.missing || []).map((m, i) => (
                <li key={i} className="border-l-2 border-red-400 pl-3" data-testid={`missing-field-${i}`}>
                  {m}
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* View document dialog */}
      <Dialog open={!!showDoc} onOpenChange={() => setShowDoc(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{showDoc?.template_name}</DialogTitle>
            <DialogDescription>
              <span className="text-[10px] tracking-widest uppercase bg-amber-100 text-amber-800 px-2 py-1">Draft — requires legal review</span>
            </DialogDescription>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-sm bg-zinc-50 border border-zinc-200 p-6 font-mono leading-relaxed">{showDoc?.content}</pre>
        </DialogContent>
      </Dialog>

      {/* Create invoice dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">New invoice</DialogTitle>
            <DialogDescription>Add line items and create the invoice draft.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {invItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-7">
                  <label className="text-overline text-zinc-500">Description</label>
                  <input
                    value={it.description}
                    onChange={(e) => {
                      const next = [...invItems];
                      next[i].description = e.target.value;
                      setInvItems(next);
                    }}
                    className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-900"
                    data-testid={`inv-item-desc-${i}`}
                  />
                </div>
                <div className="col-span-4">
                  <label className="text-overline text-zinc-500">Amount (ZAR)</label>
                  <input
                    type="number"
                    value={it.amount}
                    onChange={(e) => {
                      const next = [...invItems];
                      next[i].amount = parseFloat(e.target.value) || 0;
                      setInvItems(next);
                    }}
                    className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-900"
                    data-testid={`inv-item-amt-${i}`}
                  />
                </div>
                <button onClick={() => setInvItems(invItems.filter((_, j) => j !== i))} className="col-span-1 h-10 grid place-items-center border border-zinc-200 hover:bg-zinc-50">
                  <Trash2 className="h-4 w-4 text-zinc-500" />
                </button>
              </div>
            ))}
            <button onClick={() => setInvItems([...invItems, { description: "", amount: 0 }])} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add line
            </button>
            <textarea
              value={invNotes}
              onChange={(e) => setInvNotes(e.target.value)}
              rows={2}
              placeholder="Notes (optional)"
              className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-900 mt-3"
            />
          </div>
          <Button onClick={createInvoice} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-invoice">
            Create invoice
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeyVal({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 p-4">
      <div className="text-overline text-zinc-500">{label}</div>
      <div className="text-sm mt-1 capitalize break-words">{value || "—"}</div>
    </div>
  );
}


const TASK_STATUSES = ["pending", "in_progress", "done", "blocked"];

function ChecklistPanel({ matterId, tasks, onChange }) {
  const [busyKey, setBusyKey] = useState(null);

  const setStatus = async (taskKey, status) => {
    setBusyKey(taskKey);
    try {
      await api.patch(`/matters/${matterId}/tasks/${taskKey}`, { status });
      toast.success(`Task → ${status.replace("_", " ")}`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const cycle = (cur) => {
    const order = ["pending", "in_progress", "done"];
    if (cur === "blocked") return "pending";
    const idx = order.indexOf(cur);
    return order[(idx + 1) % order.length];
  };

  const done = tasks.filter((t) => t.status === "done").length;
  const blocked = tasks.filter((t) => t.status === "blocked").length;

  if (tasks.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 p-10 text-center text-zinc-500">
        <CheckCircle2 className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
        <p>No checklist for this matter. (Matters opened from the calculator auto-generate a checklist.)</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Total tasks" value={tasks.length} />
        <Stat label="Complete" value={`${done} / ${tasks.length}`} accent={done === tasks.length ? "good" : null} />
        <Stat label="Blocked" value={blocked} accent={blocked > 0 ? "bad" : null} />
      </div>

      <ol className="space-y-2" data-testid="admin-task-list">
        {[...tasks]
          .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0) || (a.order || 0) - (b.order || 0))
          .map((t, i) => {
          const meta = ADMIN_TASK_META[t.status] || ADMIN_TASK_META.pending;
          const Icon = meta.icon;
          const busy = busyKey === t.key;
          const pCls =
            t.priority_level === "urgent" ? "bg-red-600 text-white" :
            t.priority_level === "high" ? "bg-amber-600 text-white" :
            t.priority_level === "medium" ? "bg-zinc-200 text-zinc-800" :
            "bg-zinc-100 text-zinc-600";
          return (
            <li
              key={t.key}
              className={`border border-zinc-200 ${meta.bg} p-4 grid grid-cols-12 gap-3 items-start`}
              data-testid={`admin-task-${t.key}`}
            >
              <div className="col-span-1 font-mono text-xs text-zinc-400 pt-1">{String(i + 1).padStart(2, "0")}</div>
              <div className="col-span-8">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{t.title}</span>
                  <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 ${pCls}`}>{t.priority_level || "medium"}</span>
                  {t.blocks_registration && (
                    <span className="text-[10px] tracking-widest uppercase bg-red-100 text-red-700 px-1.5 py-0.5">blocker</span>
                  )}
                  <span className="text-[10px] tracking-widest uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5">{t.category}</span>
                  {(t.estimated_days_min || t.estimated_days_max) && (
                    <span className="text-[10px] tracking-widest uppercase bg-zinc-50 text-zinc-500 px-1.5 py-0.5 font-mono">
                      {t.estimated_days_min}–{t.estimated_days_max}d
                    </span>
                  )}
                </div>
                {t.detail && <p className="mt-1 text-xs text-zinc-600">{t.detail}</p>}
                {t.source && <p className="mt-1 text-[10px] font-mono italic text-zinc-500">Source: {t.source}</p>}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => setStatus(t.key, cycle(t.status))}
                  disabled={busy}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 border ${meta.btnClass} hover:opacity-80 transition-opacity`}
                  data-testid={`task-cycle-${t.key}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${t.status === "in_progress" ? "animate-spin" : ""}`} />
                  {meta.label}
                </button>
                <select
                  value={t.status}
                  onChange={(e) => setStatus(t.key, e.target.value)}
                  disabled={busy}
                  className="text-xs border border-zinc-200 px-1.5 py-1 bg-white"
                  data-testid={`task-select-${t.key}`}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const ADMIN_TASK_META = {
  pending: { icon: Clock, label: "Pending", bg: "bg-white", btnClass: "border-zinc-200 text-zinc-700 bg-white" },
  in_progress: { icon: Loader2, label: "In progress", bg: "bg-blue-50", btnClass: "border-[#0055FF] text-[#0055FF] bg-white" },
  done: { icon: CheckCircle2, label: "Done", bg: "bg-green-50", btnClass: "border-green-600 text-green-700 bg-white" },
  blocked: { icon: Ban, label: "Blocked", bg: "bg-red-50", btnClass: "border-red-600 text-red-700 bg-white" },
};

function Stat({ label, value, accent }) {
  const bg = accent === "good" ? "bg-green-50 border-green-200" : accent === "bad" ? "bg-red-50 border-red-200" : "bg-white border-zinc-200";
  return (
    <div className={`p-4 border ${bg}`}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className="font-serif text-3xl font-medium mt-1">{value}</div>
    </div>
  );
}


// Phase 5.6 — Risks banner above all tabs (red for high, amber for medium, slate for low)
function RiskBanner({ risks }) {
  if (!risks || risks.length === 0) return null;
  const high = risks.filter((r) => r.severity === "high");
  const med  = risks.filter((r) => r.severity === "medium");
  const low  = risks.filter((r) => r.severity === "low");
  const tone =
    high.length ? "bg-red-50 border-red-200 text-red-900" :
    med.length  ? "bg-amber-50 border-amber-200 text-amber-900" :
                  "bg-zinc-50 border-zinc-200 text-zinc-700";
  return (
    <div className={`border ${tone} px-5 py-4 mb-6`} data-testid="risk-banner">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="font-serif text-lg font-medium">⚠ {risks.length} risk{risks.length === 1 ? "" : "s"} detected</span>
          <span className="text-xs">
            {high.length ? `${high.length} high` : ""}
            {high.length && (med.length || low.length) ? " · " : ""}
            {med.length ? `${med.length} medium` : ""}
            {med.length && low.length ? " · " : ""}
            {low.length ? `${low.length} low` : ""}
          </span>
        </div>
        <span className="text-xs italic">See "Entities & risks" tab for details and mitigation.</span>
      </div>
    </div>
  );
}

// Phase 5.6 — Read-only entities + risks viewer (lawyer sees what the AI captured)
// Phase 6B — also offers a deterministic doc-gen affordance on Next Actions whose
//            label maps to a known canonical template.
const ACTION_TO_TEMPLATE_FRONT = [
  ["draft anc",               "antenuptial_contract"],
  ["antenuptial",             "antenuptial_contract"],
  ["sign anc",                "antenuptial_contract"],
  ["draft will",              "will"],
  ["draft engagement letter", "engagement_letter_conveyancing"],
  ["engagement letter",       "engagement_letter_conveyancing"],
];
function templateKeyForAction(label) {
  if (!label) return null;
  const low = label.toLowerCase();
  for (const [needle, key] of ACTION_TO_TEMPLATE_FRONT) if (low.includes(needle)) return key;
  return null;
}

function EntitiesPanel({ matter, onGenerate, generating }) {
  const ent = matter.entities || {};
  const risks = matter.risks || [];
  // Phase 5.6 unified schema: OTP now lives inside transaction.otp.
  // Surface it as its own tile for lawyer review by promoting it from transaction.
  const transaction = ent.transaction || {};
  const otp = transaction.otp || ent.otp || null;            // legacy fallback for old matters
  const transactionWithoutOtp = (() => {
    if (!transaction || typeof transaction !== "object") return transaction;
    const { otp: _otp, ...rest } = transaction;
    return rest;
  })();
  const sections = [
    ["Persons",      ent.persons,             (v) => Array.isArray(v) && v.length > 0],
    ["Marriage",     ent.marriage,            (v) => v && Object.keys(v).length > 0],
    ["Properties",   ent.properties,          (v) => Array.isArray(v) && v.length > 0],
    ["Transaction",  transactionWithoutOtp,   (v) => v && Object.keys(v).length > 0],
    ["OTP",          otp,                     (v) => v && Object.keys(v).length > 0],
    ["Trust",        ent.trust,               (v) => v && Object.keys(v).length > 0],
    ["Will",         ent.will,                (v) => v && Object.keys(v).length > 0],
    ["Estate",       ent.estate,              (v) => v && Object.keys(v).length > 0],
  ].filter(([, v, p]) => p(v));

  return (
    <div className="space-y-6" data-testid="entities-panel">
      {/* Risks (always at the top) */}
      <div className="bg-white border border-zinc-200">
        <div className="px-6 py-3 border-b border-zinc-100 text-overline text-zinc-500 flex items-center justify-between">
          <span>Risks &amp; legal flags</span>
          <span className="text-xs">{risks.length} total</span>
        </div>
        {risks.length === 0 ? (
          <div className="px-6 py-10 text-sm text-zinc-500 text-center">No risks detected by the intake engine or rule validator.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {risks.map((r, i) => {
              const sev = r.severity || "low";
              const tone =
                sev === "high"   ? "border-l-red-500 bg-red-50/40" :
                sev === "medium" ? "border-l-amber-500 bg-amber-50/40" :
                                   "border-l-zinc-400 bg-zinc-50/30";
              const pillTone =
                sev === "high"   ? "bg-red-600 text-white" :
                sev === "medium" ? "bg-amber-500 text-white" :
                                   "bg-zinc-400 text-white";
              return (
                <li key={i} className={`border-l-4 ${tone} px-5 py-4`} data-testid={`risk-row-${r.code}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${pillTone}`}>{sev}</span>
                    <div className="font-medium text-sm flex-1 min-w-[200px]">{r.title}</div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.code}</span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-700">{r.detail}</p>
                  {r.mitigation && <p className="mt-1 text-xs italic text-zinc-600"><b>Suggested mitigation:</b> {r.mitigation}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Captured entities */}
      <div className="grid lg:grid-cols-2 gap-6">
        {sections.length === 0 ? (
          <div className="lg:col-span-2 bg-white border border-zinc-200 p-10 text-sm text-zinc-500 text-center">
            No canonical entities captured for this matter yet. Entities are populated when the AI intake is completed.
          </div>
        ) : sections.map(([title, value]) => (
          <div key={title} className="bg-white border border-zinc-200" data-testid={`entity-${title.toLowerCase()}`}>
            <div className="px-5 py-3 border-b border-zinc-100 text-overline text-zinc-500">{title}</div>
            <div className="p-5">
              {Array.isArray(value) ? (
                <ul className="space-y-3">
                  {value.map((v, i) => (
                    <li key={i} className="border-l-2 border-[#5a6b86] pl-3">
                      <EntityKV obj={v} />
                    </li>
                  ))}
                </ul>
              ) : <EntityKV obj={value} />}
            </div>
          </div>
        ))}
      </div>

      {/* Phase 6A — Next actions (executable plan from validate_envelope) */}
      {Array.isArray(matter.next_actions) && matter.next_actions.length > 0 && (
        <div className="bg-white border border-zinc-200" data-testid="next-actions-panel">
          <div className="px-6 py-3 border-b border-zinc-100 text-overline text-zinc-500 flex items-center justify-between">
            <span>Next actions</span>
            <span className="text-xs">{matter.next_actions.length} total</span>
          </div>
          <ul className="divide-y divide-zinc-100">
            {matter.next_actions.map((a, i) => {
              const tplKey = templateKeyForAction(a.label);
              return (
                <li key={i} className={`px-5 py-3 border-l-4 ${a.blocking ? "border-l-red-500 bg-red-50/30" : "border-l-zinc-200"}`}
                    data-testid={`next-action-${i}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${a.blocking ? "bg-red-600 text-white" : "bg-zinc-200 text-zinc-700"}`}>
                      {a.owner}
                    </span>
                    <div className="font-medium text-sm flex-1 min-w-[200px]">{a.label}</div>
                    {a.deadline && <span className="text-[10px] uppercase tracking-widest text-zinc-500">by {a.deadline}</span>}
                    {tplKey && onGenerate && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none h-7 text-[11px]"
                        onClick={() => onGenerate(tplKey, a.label)}
                        disabled={!!generating}
                        data-testid={`next-action-generate-${i}`}
                      >
                        <FileText className="h-3 w-3 mr-1" /> Generate now
                      </Button>
                    )}
                  </div>
                  {a.rationale && <p className="mt-1 text-xs text-zinc-600">{a.rationale}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Phase 6A — Audit trail (one line per extraction event) */}
      {Array.isArray(matter.audit_events) && matter.audit_events.length > 0 && (
        <div className="bg-white border border-zinc-200" data-testid="audit-panel">
          <div className="px-6 py-3 border-b border-zinc-100 text-overline text-zinc-500">Audit trail</div>
          <ul className="divide-y divide-zinc-100">
            {matter.audit_events.map((ev) => (
              <li key={ev.id} className="px-5 py-3 text-sm" data-testid={`audit-event-${ev.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-2 py-1">{ev.kind}</span>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium">{ev.summary}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {ev.actor || "system"} · {new Date(ev.created_at).toLocaleString()}
                    </div>
                    {(ev.risks_added?.length > 0 || ev.actions_added?.length > 0) && (
                      <div className="mt-2 text-[11px] text-zinc-600 space-y-0.5">
                        {ev.risks_added?.length > 0 && (
                          <div><b>Risks added:</b> {ev.risks_added.join(", ")}</div>
                        )}
                        {ev.actions_added?.length > 0 && (
                          <div><b>Actions added:</b> {ev.actions_added.join("; ")}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EntityKV({ obj }) {
  if (!obj || typeof obj !== "object") return <span className="text-sm text-zinc-500">—</span>;
  const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return <span className="text-sm text-zinc-500">—</span>;
  return (
    <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-[10px] uppercase tracking-widest text-zinc-500 col-span-1 self-baseline">{k.replace(/_/g, " ")}</dt>
          <dd className="col-span-2 font-mono text-xs break-words">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}



// Phase 8.2 — Ownership bar above the tabs. Shows the current owner and
// exposes "Assign to me" (lawyer/back_office/admin), a "Change owner"
// dropdown (admin only), and an "Unassign" affordance for current owner.
function OwnerBar({ matter, me, assignableUsers, saving, onSet }) {
  const ownerId = matter.owner_user_id || null;
  const ownerLabel = matter.owner_label || null;
  const canSelfAssign = me && ["lawyer", "back_office", "admin"].includes(me.role);
  const isAdmin = me?.role === "admin";
  const iAmOwner = ownerId && me && ownerId === me.id;
  const canUnassign = iAmOwner || isAdmin;

  return (
    <div
      className="bg-zinc-50 border border-zinc-200 px-5 py-3 mb-8 flex items-center gap-4 flex-wrap"
      data-testid="owner-bar"
    >
      <div className="flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-zinc-500" />
        <span className="text-overline text-zinc-500">Owner</span>
      </div>
      <div className="flex-1 min-w-[180px]">
        {ownerId ? (
          <span data-testid="owner-current" className="text-sm font-medium text-zinc-900">
            {ownerLabel || ownerId.slice(0, 8)}
          </span>
        ) : (
          <span data-testid="owner-unassigned" className="text-sm text-zinc-500 italic">
            Unassigned
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {canSelfAssign && !iAmOwner && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-none"
            disabled={saving}
            onClick={() => onSet(me.id)}
            data-testid="assign-to-me-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Assign to me"}
          </Button>
        )}
        {isAdmin && (
          <Select
            value={ownerId || "__unassigned__"}
            onValueChange={(v) => onSet(v === "__unassigned__" ? null : v)}
            disabled={saving || assignableUsers.length === 0}
          >
            <SelectTrigger className="w-56 rounded-none" data-testid="owner-change-select">
              <SelectValue placeholder={assignableUsers.length === 0 ? "Loading…" : "Change owner"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
              {assignableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name || u.email} · {u.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {canUnassign && ownerId && !isAdmin && (
          <Button
            size="sm"
            variant="ghost"
            className="rounded-none text-xs"
            disabled={saving}
            onClick={() => onSet(null)}
            data-testid="unassign-btn"
          >
            Unassign
          </Button>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// P0 — Legal Compliance: read-only checklist + matter-close affordance.
// Reads `matter.legal_readiness` already embedded in `GET /matters/:id`.
// ---------------------------------------------------------------------------
function LegalReadinessPanel({ matterId, matter, onChanged }) {
  const readiness = matter.legal_readiness;
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing]   = useState(false);
  const [closeError, setCloseError] = useState(null);

  if (!readiness) return null;

  const ready = readiness.ready;
  const closed = matter.legal_status === "closed";
  const statusLabel = matter.legal_status_label || matter.legal_status;
  const completePct = readiness.total_count
    ? Math.round((readiness.complete_count / readiness.total_count) * 100)
    : 0;

  const closeMatter = async () => {
    setCloseError(null);
    setClosing(true);
    try {
      await api.post(`/admin/matters/${matterId}/close`);
      toast.success("Matter closed");
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        setCloseError(detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setClosing(false);
    }
  };

  const headerTone = closed
    ? "bg-zinc-100 border-zinc-300 text-zinc-700"
    : ready
      ? "bg-emerald-50 border-emerald-300 text-emerald-900"
      : "bg-amber-50 border-amber-300 text-amber-900";

  const pillTone = closed
    ? "bg-zinc-500 text-white"
    : ready
      ? "bg-emerald-600 text-white"
      : "bg-amber-600 text-white";

  return (
    <div
      className={`border ${headerTone} mb-6`}
      data-testid="legal-readiness-panel"
    >
      <div className="px-5 py-4 flex items-center gap-4 flex-wrap">
        <ShieldCheck className="h-5 w-5" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-overline opacity-70">Legal readiness</div>
          <div className="flex items-baseline gap-3 flex-wrap mt-1">
            <span
              className={`text-[10px] tracking-widest uppercase px-2 py-1 ${pillTone}`}
              data-testid="legal-readiness-status-pill"
            >
              {closed ? "Closed" : ready ? "Legally ready" : "Not legally ready"}
            </span>
            <span className="text-xs">
              {readiness.complete_count} of {readiness.total_count} requirements ·
              {" "}{completePct}%
            </span>
            <span className="text-xs italic opacity-70">
              Legal status: <b className="not-italic">{statusLabel}</b>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-none bg-white"
            onClick={() => setExpanded((s) => !s)}
            data-testid="legal-readiness-toggle"
          >
            {expanded ? "Hide checklist" : "View checklist"}
          </Button>
          {!closed && (
            <Button
              size="sm"
              className={`rounded-none ${ready ? "bg-emerald-700 hover:bg-emerald-800" : "bg-zinc-300 cursor-not-allowed"} text-white`}
              onClick={closeMatter}
              disabled={!ready || closing}
              data-testid="close-matter-btn"
              title={ready ? "Close this matter" : "Cannot close — legal requirements missing"}
            >
              {closing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : ready
                  ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  : <Lock className="h-3.5 w-3.5 mr-1" />}
              {closing ? "Closing…" : ready ? "Close matter" : "Close (locked)"}
            </Button>
          )}
        </div>
      </div>

      {closeError && (
        <div className="border-t border-amber-300 bg-amber-100/60 px-5 py-3 text-xs text-amber-900"
             data-testid="close-error-banner">
          <div className="flex items-center gap-2 font-semibold">
            <AlertCircle className="h-3.5 w-3.5" /> {closeError.message}
          </div>
          <ul className="mt-1 list-disc pl-5">
            {(closeError.missing || []).map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      )}

      {expanded && (
        <div className="border-t border-zinc-200 bg-white">
          {(readiness.groups || []).map((group) => (
            <div key={group.id} className="border-b border-zinc-100 last:border-b-0"
                 data-testid={`legal-admin-group-${group.id}`}>
              <div className="px-5 py-2 bg-zinc-50 text-overline text-zinc-600 flex items-center justify-between">
                <span>{group.label}</span>
                <span className="text-[10px] tracking-widest uppercase text-zinc-500">
                  {group.items.filter((i) => i.satisfied).length} / {group.items.length}
                </span>
              </div>
              <ul className="divide-y divide-zinc-100">
                {group.items.map((it) => (
                  <li
                    key={it.id}
                    className="px-5 py-3 flex items-start gap-3"
                    data-testid={`legal-admin-req-${it.id}`}
                  >
                    {it.satisfied
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      : <AlertCircle  className="h-4 w-4 text-amber-600  mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900">{it.label}</div>
                      {it.detail && (
                        <div className="text-xs text-zinc-500 mt-0.5">{it.detail}</div>
                      )}
                    </div>
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 shrink-0 ${
                      it.satisfied
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {it.satisfied ? "Complete" : "Required"}
                    </span>
                    {!it.satisfied && it.blocks_doc_gen && (
                      <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 bg-red-100 text-red-700 shrink-0">
                        blocks doc gen
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

